/**
 * Resteem Service
 *
 * Provides resteem (reblog) functionality using dsteem SDK.
 * Resteem broadcasts a custom_json operation to the Steem blockchain.
 */

import { PrivateKey, type Operation } from '@steempro/dsteem'
import { getSteemitClient } from './client'

export interface ResteemParams {
  account: string
  postingKey: string
  author: string
  permlink: string
}

/**
 * Resteems (reblogs) a post to the user's blog
 *
 * Uses the 'follow' custom_json operation with 'reblog' action
 */
export async function resteem(params: ResteemParams): Promise<{
  success: boolean
  error?: string
}> {
  const { account, postingKey, author, permlink } = params

  // Cannot resteem own posts
  if (account === author) {
    return {
      success: false,
      error: 'Cannot resteem your own posts',
    }
  }

  try {
    const client = getSteemitClient()
    const privateKey = PrivateKey.fromString(postingKey)

    // Create the custom_json operation for reblog
    const operation: Operation = [
      'custom_json',
      {
        required_auths: [],
        required_posting_auths: [account],
        id: 'follow',
        json: JSON.stringify(['reblog', { account, author, permlink }]),
      },
    ]

    const result = await client.broadcast.sendOperations([operation], privateKey)

    if (result) {
      return { success: true }
    }

    return {
      success: false,
      error: 'Failed to broadcast resteem operation',
    }
  } catch (error) {
    console.error('Error resteeeming post:', error)

    // Handle specific blockchain errors
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'

    // Check for duplicate reblog error
    if (errorMessage.includes('Account has already reblogged')) {
      return {
        success: false,
        error: 'You have already resteemed this post',
      }
    }

    return {
      success: false,
      error: errorMessage,
    }
  }
}

/**
 * Checks if a user has already resteemed a post
 * Note: This requires fetching the user's blog and checking
 */
export async function hasResteemed(
  account: string,
  author: string,
  permlink: string
): Promise<boolean> {
  try {
    const client = getSteemitClient()
    const result = await client.database.call('get_reblogged_by', [author, permlink])

    if (Array.isArray(result)) {
      return result.includes(account)
    }

    return false
  } catch (error) {
    console.error('Error checking resteem status:', error)
    return false
  }
}
